import { useEffect, useRef } from 'react';
import { keyframes } from '@mui/material';

/**
 * Duration (ms) the highlight pulse runs before onComplete fires.
 * Kept in sync with the keyframe animation length below.
 */
const HIGHLIGHT_DURATION_MS = 900;

/**
 * Pulse animation for block-level items (cards, compact rows).
 * Box-shadow glow + subtle background tint.
 */
export const blockPulseHighlight = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 213, 79, 0); background-color: transparent; }
  25% { box-shadow: 0 0 8px 3px rgba(255, 213, 79, 0.6); background-color: rgba(255, 213, 79, 0.12); }
  50% { box-shadow: 0 0 0 0 rgba(255, 213, 79, 0); background-color: transparent; }
  75% { box-shadow: 0 0 8px 3px rgba(255, 213, 79, 0.6); background-color: rgba(255, 213, 79, 0.12); }
  100% { box-shadow: 0 0 0 0 rgba(255, 213, 79, 0); background-color: transparent; }
`;

/**
 * Pulse animation for table rows (list view). Box-shadow doesn't render well
 * on <tr>, so this variant uses background tint only.
 */
export const rowPulseHighlight = keyframes`
  0% { background-color: transparent; }
  25% { background-color: rgba(255, 213, 79, 0.25); }
  50% { background-color: transparent; }
  75% { background-color: rgba(255, 213, 79, 0.25); }
  100% { background-color: transparent; }
`;

interface UseHighlightScrollReturn<T extends HTMLElement> {
  /** Attach to the element that should scroll into view when highlighted. */
  ref: React.RefObject<T>;
}

/**
 * Shared highlight-and-scroll behavior for any bookmark view (card, compact,
 * list, or future views like favorites/recents).
 *
 * When `highlighted` becomes true, scrolls the referenced element into view,
 * then fires `onComplete` after the pulse animation finishes so the caller can
 * clear the highlight state. Works for any element type and any caller — the
 * DOM ref stays local to the item (correct across mount/unmount) while the
 * "which bookmark is highlighted" state lives in the caller (e.g. CategoryContext).
 *
 * The effect runs after the component mounts and commits to the DOM, so the ref
 * is guaranteed to be populated even when the item renders after an async data
 * load. This is what makes the scroll reliable regardless of cache vs. network.
 *
 * Pair with `blockPulseHighlight` (cards/compact) or `rowPulseHighlight` (list)
 * for the visual pulse, applied conditionally on the same `highlighted` flag.
 */
export function useHighlightScroll<T extends HTMLElement = HTMLElement>(
  highlighted: boolean | undefined,
  onComplete?: () => void,
): UseHighlightScrollReturn<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (highlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        onComplete?.();
      }, HIGHLIGHT_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [highlighted, onComplete]);

  return { ref };
}
